// MS Security CVEs - Microsoft Security Vulnerabilities
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import '../../App.css';
import './MSSecurityCVEs.css';
import Footer from '../Footer';

export default function MSSecurityCVEs() {
  const history = useHistory();
  const [cveData, setCveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    fetchMSRCUpdates();
  }, [selectedYear, selectedMonth]);

  const fetchMSRCUpdates = async () => {
    try {
      setLoading(true);
      setError(null);

      // Route through backend proxy to avoid CORS
      const response = await fetch(`/api/msrc/${selectedYear}/${selectedMonth}`);

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.Vulnerability && Array.isArray(data.Vulnerability)) {
        setCveData(data.Vulnerability);
      } else {
        setCveData([]);
      }
    } catch (err) {
      console.error('Error fetching MSRC data:', err);
      setError(err.message || 'Failed to load Microsoft Security CVE data. Please try again later.');
      setCveData([]);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    if (!severity) return '#b0b0b0';
    const sev = severity.toLowerCase();
    if (sev.includes('critical')) return '#FF6B6B';
    if (sev.includes('important')) return '#FFA500';
    if (sev.includes('moderate')) return '#FFC107';
    if (sev.includes('low')) return '#4BC4B9';
    return '#b0b0b0';
  };

  const getSeverityLabel = (severity) => {
    if (!severity) return 'Unknown';
    const sev = severity.toLowerCase();
    if (sev.includes('critical')) return 'Critical';
    if (sev.includes('important')) return 'Important';
    if (sev.includes('moderate')) return 'Moderate';
    if (sev.includes('low')) return 'Low';
    return severity;
  };

  const renderYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2016; year--) {
      years.push(year);
    }
    return years.map(year => (
      <option key={year} value={year}>{year}</option>
    ));
  };

  const renderMonthOptions = () => {
    const months = [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' }
    ];
    return months.map(month => (
      <option key={month.value} value={month.value}>{month.label}</option>
    ));
  };

  if (loading) {
    return (
      <div className="ms-cve-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading Microsoft Security CVEs...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ms-cve-container">
        <div className="ms-cve-header">
          <button onClick={() => history.push('/tools')} className="btn-back-inline">
            <i className="fas fa-arrow-left"></i> Back to Tools
          </button>
          <h1>
            <i className="fas fa-shield-alt"></i> Microsoft Security CVEs
          </h1>
          <p className="subtitle">Latest security vulnerabilities and updates from Microsoft</p>
        </div>

        <div className="cve-filters">
          <div className="filter-group">
            <label htmlFor="year-select">
              <i className="fas fa-calendar"></i> Year:
            </label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="filter-select"
            >
              {renderYearOptions()}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="month-select">
              <i className="fas fa-calendar-alt"></i> Month:
            </label>
            <select
              id="month-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="filter-select"
            >
              {renderMonthOptions()}
            </select>
          </div>
          <button onClick={fetchMSRCUpdates} className="btn-refresh">
            <i className="fas fa-sync"></i> Refresh
          </button>
        </div>

        <div className="cve-info-banner">
          <i className="fas fa-info-circle"></i>
          <p>
            Data provided by <a href="https://msrc.microsoft.com/update-guide" target="_blank" rel="noopener noreferrer">Microsoft Security Response Center</a>
            {cveData.length > 0 && ` - Showing ${cveData.length} vulnerabilities`}
          </p>
        </div>

        {error && (
          <div className="error-banner">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
          </div>
        )}

        {!error && cveData.length === 0 && !loading && (
          <div className="no-data-state">
            <i className="fas fa-shield-alt"></i>
            <h2>No CVEs Found</h2>
            <p>No security updates available for the selected period.</p>
          </div>
        )}

        {!error && cveData.length > 0 && (
          <div className="cve-grid">
            {cveData.map((vuln, index) => {
              const cveId = vuln.CVE || `CVE-${selectedYear}-${index}`;
              const title = vuln.Title?.Value || 'Untitled Vulnerability';
              const notes = vuln.Notes || [];
              const description = notes.find(n => n.Type === 0)?.Value || 'No description available';
              const threats = vuln.Threats || [];
              const severity = threats.find(t => t.Type === 3)?.Description?.Value || 'Unknown';
              const exploited = threats.find(t => t.Type === 1)?.Description?.Value || '';

              return (
                <div key={index} className="cve-card">
                  <div className="cve-card-header">
                    <div className="cve-id-section">
                      <h3>{cveId}</h3>
                      <span 
                        className="severity-badge" 
                        style={{ 
                          backgroundColor: `${getSeverityColor(severity)}22`,
                          color: getSeverityColor(severity),
                          borderColor: getSeverityColor(severity)
                        }}
                      >
                        {getSeverityLabel(severity)}
                      </span>
                    </div>
                    {exploited && exploited.toLowerCase().includes('exploited') && (
                      <span className="exploited-badge">
                        <i className="fas fa-exclamation-triangle"></i> Exploited
                      </span>
                    )}
                  </div>

                  <div className="cve-card-body">
                    <h4>{title}</h4>
                    <p className="cve-description">{description}</p>

                    {vuln.ProductStatuses && vuln.ProductStatuses.length > 0 && (
                      <div className="affected-products">
                        <strong>
                          <i className="fas fa-boxes"></i> Affected Products:
                        </strong>
                        <p className="product-count">
                          {vuln.ProductStatuses[0]?.ProductID?.length || 0} products affected
                        </p>
                      </div>
                    )}

                    <div className="cve-actions">
                      <a
                        href={`https://msrc.microsoft.com/update-guide/vulnerability/${cveId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cve-link"
                      >
                        <i className="fas fa-external-link-alt"></i> View Details
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
